import React from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { connect } from 'react-redux';
import {
  fetchClubEvent,
  updateAttendance as updateAttendanceAction,
  fetchAttendance,
} from '../../store/events/actions';
import { getMessage } from '../../store/activity-stream/selectors';
import {
  isLoadingEvent,
  isLoadingAttendance,
  hasAttemptedEventLoad,
  hasLoadedAttendance,
  getEvent,
  getEventAttendances,
  getEventAttendees,
} from '../../store/events/selectors';
import {
  isAssociated,
  isLoadingNetworkPanel as isLoadingNetworkPanelSelector,
} from '../../store/membership/selectors';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import ClubEvent from '../../components/club-event';
import SeoHead from '../../components/seo';
import { isLoggedIn as isLoggedInSelector } from '../../store/auth/selectors';

class ClubEventPage extends React.Component {
  static async getInitialProps({ club, query, isServer, reduxStore, req }) {
    const eventId = parseInt(query.id, 10);
    const initialProps = {
      activeSection: 'calendar',
      eventId,
      namespacesRequired: ['common'],
      pageType: 'calendar.event',
    };
    if (!isServer) {
      return initialProps;
    }

    try {
      await reduxStore.dispatch(fetchClubEvent(club.id, eventId, req));
      return initialProps;
    } catch (err) {
      return initialProps;
    }
  }

  componentDidMount() {
    this.loadIfRequired();
    this.loadAttendanceIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { isLoggedIn, eventId } = this.props;
    const { isLoggedIn: prevIsLoggedIn, eventId: previousEventId } = prevProps;
    if (eventId !== previousEventId || isLoggedIn !== prevIsLoggedIn) {
      this.loadIfRequired();
      this.loadAttendanceIfRequired();
    }
  }

  loadAttendanceIfRequired = () => {
    const { hasLoadedMemberAttendance, isLoadingMemberAttendance, isLoggedIn } = this.props;
    if (isLoggedIn && !hasLoadedMemberAttendance && !isLoadingMemberAttendance) {
      const {
        club: { id: clubId },
        eventId,
        loadMemberAttendance,
      } = this.props;
      loadMemberAttendance(clubId, eventId);
    }
  };

  loadIfRequired = () => {
    const {
      hasLoaded,
      isLoading,
      loadEvent,
      eventId,
      club: { id: clubId },
    } = this.props;

    if (!hasLoaded && !isLoading) {
      loadEvent(clubId, eventId);
    }
  };

  onMarkAsAttendingHandler = (member) => {
    const {
      eventId,
      club: { id: clubId },
      updateAttendance,
    } = this.props;
    updateAttendance(clubId, eventId, true, member);
  };

  onMarkAsNotAttendingHandler = (member) => {
    const {
      eventId,
      club: { id: clubId },
      updateAttendance,
    } = this.props;

    updateAttendance(clubId, eventId, false, member);
  };

  render() {
    const {
      club,
      attendees,
      event,
      eventId,
      isLoading,
      isLoggedIn,
      isLoadingNetworkPanel,
      isLoadingMemberAttendance,
      memberAttendances,
      message,
      userIsAssociatedToClub,
    } = this.props;

    let pageTitle;
    let startTime;
    const eventProps = {};

    if (event) {
      ({
        id: eventProps.id,
        author: eventProps.author,
        html: eventProps.html,
        image: eventProps.image,
        title: eventProps.title,
        start: eventProps.start,
        end: eventProps.end,
        recurrence: eventProps.recurrence,
      } = event);
      startTime = DateTime.fromISO(eventProps.start)
        .setZone(club.timezone)
        .toFormat('cccc d MMMM yyyy, HH:mm');
      ({ title: pageTitle } = event);
    } else if (message) {
      ({ author: eventProps.author, image: eventProps.image, title: eventProps.title } = message);
      ({ title: pageTitle } = message);
    }

    return (
      <>
        <SeoHead
          club={club}
          descriptionTranslationKey="common:event_meta_description"
          route="calendar.event"
          routeParams={{
            id: eventId,
            _title: pageTitle,
          }}
          title={pageTitle}
          translationValues={{ startTime }}
        />
        <ClubEvent
          club={club}
          eventId={eventId}
          event={eventProps}
          attendees={attendees}
          memberAttendances={memberAttendances}
          isLoading={isLoading}
          isLoadingMemberAttendance={isLoadingNetworkPanel || isLoadingMemberAttendance}
          isLoggedIn={isLoggedIn}
          onMarkAsAttending={this.onMarkAsAttendingHandler}
          onMarkAsNotAttending={this.onMarkAsNotAttendingHandler}
          userIsAssociatedToClub={userIsAssociatedToClub}
        />
      </>
    );
  }
}

ClubEventPage.defaultProps = {
  event: null,
  message: null,
};

ClubEventPage.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  attendees: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  event: PropTypes.shape({
    html: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string,
  }),
  eventId: PropTypes.number.isRequired,
  hasLoaded: PropTypes.bool.isRequired,
  hasLoadedMemberAttendance: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMemberAttendance: PropTypes.bool.isRequired,
  isLoadingNetworkPanel: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  loadMemberAttendance: PropTypes.func.isRequired,
  loadEvent: PropTypes.func.isRequired,
  message: PropTypes.shape({
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  memberAttendances: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      isAttending: PropTypes.bool.isRequired,
      memberId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  updateAttendance: PropTypes.func.isRequired,
  userIsAssociatedToClub: PropTypes.bool.isRequired,
};

const dispatchToProps = {
  loadEvent: fetchClubEvent,
  loadMemberAttendance: fetchAttendance,
  updateAttendance: updateAttendanceAction,
};

const mapStateToProps = (state, ownProps) => ({
  hasLoaded: hasAttemptedEventLoad(state, ownProps.eventId),
  hasLoadedMemberAttendance: hasLoadedAttendance(state, ownProps.eventId),
  isLoading: isLoadingEvent(state, ownProps.eventId),
  isLoadingMemberAttendance: isLoadingAttendance(state, ownProps.eventId),
  isLoadingNetworkPanel: isLoadingNetworkPanelSelector(state, ownProps.club.id),
  isLoggedIn: isLoggedInSelector(state),
  event: getEvent(state, ownProps.eventId),
  message: getMessage(state, `club_event-${ownProps.eventId}`),
  memberAttendances: getEventAttendances(state, ownProps.eventId),
  attendees: getEventAttendees(state, ownProps.eventId),
  userIsAssociatedToClub: isAssociated(state, ownProps.club.id),
});

export default connect(mapStateToProps, dispatchToProps)(ClubEventPage);
